import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import config from 'config';
import { createScriptNode } from 'utils/dom';
import { useDidUpdateEffect } from 'utils/hooks';

const UserlaneScriptElementId = 'userlane-script';
const w: any = window;

const subsTagsMap: Record<number, string> = {
  10: 'trial',
  246: 'test',
  30: 'Basic',
  31: 'Plus',
  32: 'Premium',
};

const generateTags = ({
  subscriptionsIds,
}: {
  subscriptionsIds: number[];
}): string[] => subscriptionsIds.map((id) => subsTagsMap[id]).filter((v) => v);

interface Props {
  accountId: number | null;
  signUpDate: Date | null;
  email: string | null;
  subscriptionsIds: number[];
}

const UserlaneWidget: React.FC<Props> = ({
  accountId,
  email,
  subscriptionsIds,
  signUpDate,
}) => {
  const intl = useIntl();
  const showCBSuccessModal = localStorage.getItem('showCBSuccessModal');
  const isScriptLoaded = useRef(false);
  const isInitialized = useRef(false);

  const tags = useMemo(
    () => generateTags({ subscriptionsIds }),
    [subscriptionsIds]
  );

  useEffect(() => {
    const preconnectLinks = [
      { rel: 'preconnect', href: 'https://cdn.userlane.com' },
      { rel: 'preconnect', href: 'https://api.userlane.com' },
      { rel: 'dns-prefetch', href: 'https://cdn.userlane.com' },
      { rel: 'dns-prefetch', href: 'https://api.userlane.com' },
    ];

    preconnectLinks.forEach(({ rel, href }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      preconnectLinks.forEach(({ rel, href }) => {
        const link = document.querySelector(
          `link[rel="${rel}"][href="${href}"]`
        );
        if (link) link.remove();
      });
    };
  }, []);

  const initializeUserlane = useCallback(() => {
    if (
      accountId &&
      w.Userlane &&
      !isInitialized.current &&
      (showCBSuccessModal === undefined || showCBSuccessModal !== 'ON')
    ) {
      w.Userlane('lang', intl.locale);
      w.Userlane('tag', accountId, tags);
      w.Userlane('identify', accountId, {
        email,
        signUpDate,
        tags,
      });
      w.Userlane('init', config.USERLANE_APP_ID);
      isInitialized.current = true;
    }
  }, [accountId, signUpDate, email, tags, intl.locale, showCBSuccessModal]);

  const loadUserlaneScript = useCallback((): void => {
    if (isScriptLoaded.current) return;

    const scriptNode = createScriptNode({
      src: 'https://cdn.userlane.com/userlane.js',
      id: UserlaneScriptElementId,
      async: true,
      defer: true,
      onload: () => {
        isScriptLoaded.current = true;
        initializeUserlane();
      },
    });
    document.body.appendChild(scriptNode);
  }, [initializeUserlane]);

  // Load script based on user interaction
  useEffect(() => {
    const loadOnInteraction = () => {
      if (!isScriptLoaded.current) {
        loadUserlaneScript();
      }
    };

    if (!isScriptLoaded.current) {
      const events = ['mousemove', 'scroll', 'click', 'keydown'];
      events.forEach((event) => {
        document.addEventListener(event, loadOnInteraction, { once: true });
      });

      const idleTimer = setTimeout(loadOnInteraction, 5000);

      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, loadOnInteraction);
        });
        clearTimeout(idleTimer);
      };
    }

    // Return empty cleanup function when script is already loaded
    return () => {};
  }, [loadUserlaneScript]);

  useDidUpdateEffect(() => {
    if (w.Userlane) {
      w.Userlane('lang', intl.locale);
    }
  }, [intl.locale]);

  useEffect(() => {
    if (w.Userlane && !email) {
      w.Userlane('hide');
    }
  }, [email]);

  return null;
};

export default memo(UserlaneWidget);
